import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/site/Footer";
import Logo from "@/components/site/Logo";

export default function CGV() {
  useEffect(() => {
    document.title = "Conditions Générales de Vente — Solmis Habitat";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white text-foreground min-h-screen">
      <header className="border-b border-border bg-white/90 backdrop-blur-md sticky top-0 z-40">
        <div className="container-wide flex h-20 items-center justify-between">
          <Link to="/" data-testid="cgv-logo" className="block">
            <Logo withTagline variant="dark" />
          </Link>
          <Link
            to="/"
            data-testid="cgv-back"
            className="inline-flex items-center gap-2 text-sm text-primary/85 hover:text-[hsl(var(--brand-terra))] transition-colors"
          >
            <ArrowLeft size={16} strokeWidth={1.6} />
            Retour à l'accueil
          </Link>
        </div>
      </header>

      <main className="py-20 md:py-28">
        <article className="container-narrow">
          <div className="flex items-center gap-3 mb-6">
            <span className="rule" />
            <span className="overline">Documents contractuels</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-primary leading-[1.05]">
            Conditions Générales de{" "}
            <span className="italic font-light text-[hsl(var(--brand-terra))]">
              Vente
            </span>
          </h1>
          <p className="mt-5 text-base text-muted-foreground">
            Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long" })}
          </p>

          <div className="mt-14 space-y-12 text-[15px] leading-relaxed text-primary/90">
            <Section index="01" title="Objet">
              <p>
                Les présentes CGV définissent les conditions dans lesquelles
                <strong> EM GROUP</strong> propose ses services via la marque
                <strong> Solmis Habitat</strong>.
              </p>
            </Section>

            <Section index="02" title="Prestations">
              <p>Les prestations incluent :</p>
              <ul className="mt-3 space-y-1.5 list-disc pl-5 text-primary/85">
                <li>Études de sol</li>
                <li>Permis de construire</li>
                <li>Accompagnement de projet</li>
              </ul>
              <p className="mt-3">
                Les prestations sont réalisées directement ou via des
                partenaires.
              </p>
            </Section>

            <Section index="03" title="Devis">
              <p>Toute prestation fait l'objet d'un devis personnalisé.</p>
              <p className="mt-3">
                Le devis doit être accepté avant toute intervention.
              </p>
            </Section>

            <Section index="04" title="Tarifs">
              <p>Les prix sont définis sur devis uniquement.</p>
            </Section>

            <Section index="05" title="Paiement">
              <p>Les modalités de paiement sont précisées sur le devis :</p>
              <ul className="mt-3 space-y-1.5 list-disc pl-5 text-primary/85">
                <li>Acompte de 60 %</li>
                <li>
                  Paiement avant dépôt du dossier à l'organisme correspondant
                  à la prestation
                </li>
              </ul>
            </Section>

            <Section index="06" title="Délais">
              <p>Les délais sont donnés à titre indicatif.</p>
            </Section>

            <Section index="07" title="Responsabilité">
              <p>
                EM GROUP s'engage à fournir des prestations conformes aux
                règles en vigueur.
              </p>
              <p className="mt-3">
                La responsabilité ne saurait être engagée en cas de mauvaise
                utilisation ou d'informations incomplètes fournies par le
                client.
              </p>
            </Section>

            <Section index="08" title="Partenaires">
              <p>
                Certaines prestations peuvent être réalisées par des
                partenaires externes spécialisés.
              </p>
            </Section>

            <Section index="09" title="Litiges">
              <p>
                En cas de litige, une solution amiable sera recherchée.
              </p>
              <p className="mt-3">
                À défaut, les tribunaux compétents seront ceux du siège
                social.
              </p>
            </Section>
          </div>

          <div className="mt-16 pt-10 border-t border-border flex flex-wrap items-center gap-4">
            <Link to="/" data-testid="cgv-back-bottom" className="btn-primary">
              <ArrowLeft size={16} strokeWidth={1.8} />
              Retour à l'accueil
            </Link>
            <Link
              to="/mentions-legales"
              className="text-sm text-primary/85 hover:text-[hsl(var(--brand-terra))] underline underline-offset-4"
            >
              Mentions légales
            </Link>
            <Link
              to="/politique-confidentialite"
              className="text-sm text-primary/85 hover:text-[hsl(var(--brand-terra))] underline underline-offset-4"
            >
              Politique de confidentialité
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}

function Section({ index, title, children }) {
  return (
    <section>
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-[hsl(var(--brand-terra))] mb-2">
        {index}
      </p>
      <h2 className="font-display text-2xl md:text-3xl text-primary leading-snug">
        {title}
      </h2>
      <span className="block h-0.5 w-10 bg-[hsl(var(--brand-terra))] mt-3 mb-5" />
      <div className="space-y-2">{children}</div>
    </section>
  );
}
